'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import useParallaxDots from '@/hooks/useParallaxDots';
import { useTheme } from 'next-themes';
import { usePathname } from 'next/navigation';

// Enhanced color palette for more vibrant lighting effect
const COLORS = [
  'bg-pink-300/90', 
  'bg-yellow-300/70', 
  'bg-blue-300/70',
  'bg-purple-300/90',
  'bg-teal-300/70'
];

type Shape = {
  id: number;
  style: Record<string, string>;
  width: number;
  height: number;
  color: string;
  opacity: number;
  parallaxSpeed: number;
  rotationDirection: number;
  rotationSpeed: number;
  blur: number;
  verticalPosition: number;
  hueRotate: number;
  animationDelay: number;
};

// export default function DecorativeDots({numShapes = 8, minSize = 90, maxSize = 240}: {numShapes?: number, minSize?: number, maxSize?: number}) {
//   return <></>;
// }

export default function DecorativeDots({numShapes = 8, minSize = 90, maxSize = 240}: {numShapes?: number, minSize?: number, maxSize?: number}) {
    useParallaxDots();
    const shapesRef = useRef<HTMLDivElement>(null);
    const [shapes, setShapes] = useState<Shape[] | null>(null);
    const [useAnimation, setUseAnimation] = useState(true);
    const { theme } = useTheme();

    const pathname = usePathname();

    const hideInRoutes = useMemo(() => {
      return ['/welcome/webview'];
    }, []);

    const shouldHide = useMemo(() => {
      return hideInRoutes.some(route => pathname.includes(route));
    }, [pathname]);

    // Handle animation of light effects
    // useEffect(() => {
    //   if (!shapes || shapes.length === 0) return;
      
    //   let animationFrameId: number;
    //   const startTime = Date.now();
      
    //   const animateShapes = () => {
    //     const elements = shapesRef.current?.querySelectorAll('[data-light-effect]');
    //     if (!elements) return;
        
    //     const elapsedTime = Date.now() - startTime;
        
    //     elements.forEach((el, index) => {
    //       const shape = shapes[index];
    //       if (!shape) return;
          
    //       // Calculate dynamic filter values based on time
    //       const timeOffset = elapsedTime + shape.animationDelay;
    //       const hueShift = shape.hueRotate + Math.sin(timeOffset / 5000) * 20;
    //       const blurChange = shape.blur + Math.sin(timeOffset / 6000) * 15;
    //       const opacityChange = shape.opacity + Math.sin(timeOffset / 8000) * 0.15;
          
    //       // Apply dynamic changes
    //       const filterStyle = `blur(${blurChange}px) hue-rotate(${hueShift}deg)`;
    //       (el as HTMLElement).style.filter = filterStyle;
    //       (el as HTMLElement).style.opacity = opacityChange.toString();
    //     });
        
    //     if (useAnimation) {
    //       animationFrameId = requestAnimationFrame(animateShapes);
    //     } else {
    //       // Stop the animation (to save power)
    //       animationFrameId = 0;
    //     }
    //   };
      
    //   animateShapes();
      
    //   return () => {
    //     if (animationFrameId) {
    //       cancelAnimationFrame(animationFrameId);
    //     }
    //   };
    // }, [shapes]);

    useEffect(() => {
      // Create evenly distributed vertical positions
      const verticalSegments = numShapes + 1;
      const segmentHeight = 100 / verticalSegments;
      
      const generated = Array.from({ length: numShapes }, (_, i) => {
        // Min size 90px as requested, up to 200px
        const baseSize = Math.floor(Math.random() * (maxSize - minSize)) + minSize;
        
        // Create slightly ellipsoid shapes by varying width and height
        const width = baseSize;
        const height = baseSize * (0.8 + Math.random() * 0.5); // 80-130% of width
        
        // Distribute shapes evenly vertically with some randomness
        const verticalPosition = segmentHeight * (i + 1) - segmentHeight / 2 + (Math.random() * 10 - 5);
        
        // Left or right side of the screen
        const leftOrRight = Math.random() > 0.5 ? 'left' : 'right';
        const horizontal = Math.floor(Math.random() * 40); // Keep closer to edges
        
        const color = COLORS[Math.floor(Math.random() * COLORS.length)];
        const opacity = 0.15 + Math.random() * 0.25; // Lower opacity for fuzzy effect
        const parallaxSpeed = Math.random() * 0.5 + 0.2;
        const rotationDirection = Math.random() > 0.5 ? 1 : -1; // Clockwise or counter-clockwise
        const rotationSpeed = Math.random() * 0.2 + 0.05; // Slower rotation for smoother effect
        const blur = Math.floor(Math.random() * 35) + 20; // Strong blur effect
        const hueRotate = Math.floor(Math.random() * 30); // Slight hue variation
        const animationDelay = Math.random() * 10000; // Random delay for animations
  
        return {
          id: i,
          style: {
            top: `${verticalPosition}%`,
            [leftOrRight]: `${horizontal}%`,
          },
          width,
          height,
          color,
          opacity,
          parallaxSpeed,
          rotationDirection,
          rotationSpeed,
          blur,
          verticalPosition,
          hueRotate,
          animationDelay,
        };
      });
  
      setShapes(generated);
    }, []);

    if (shouldHide) {
      return <></>;
    }
    
    return (
        <div ref={shapesRef} className="absolute inset-0 overflow-hidden pointer-events-none">
          {shapes && shapes.map(({ 
            id, 
            style, 
            width, 
            height, 
            color, 
            opacity, 
            parallaxSpeed, 
            rotationDirection,
            rotationSpeed,
            blur,
            hueRotate
          }) => (
            <div
              key={id}
              className={`absolute ${color} rounded-full will-change-transform`}
              style={{
                ...style,
                width: `${width}px`,
                height: `${height}px`,
                opacity,
                filter: `blur(${blur}px) hue-rotate(${hueRotate}deg)`,
                transform: 'translateY(0) rotate(0deg)',
                boxShadow: '0 0 60px 10px rgba(255, 255, 255, 0.2) inset',
                '--speed': parallaxSpeed,
                '--rotation-dir': rotationDirection,
                '--rotation-speed': rotationSpeed,
                zIndex: 0,
              } as React.CSSProperties}
              data-parallax
              data-rotation
              data-light-effect
            ></div>
          ))}
          
          {/* Bottom fade overlay */}
          <div 
            className="absolute z-1 bottom-0 left-0 right-0 h-[150px] pointer-events-none bg-gradient-to-b bg-gradient-to-b from-transparent dark:to-black"            
          />
      </div>
  );
} 